import type { ReactElement } from 'react';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TruncatedText } from '@/components/shared/TruncatedText';

interface MCPVersionDisplayProps {
  versions: (string | null)[];
}

export function MCPVersionCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return (
    <span className="block w-full min-w-0 text-xs text-muted-foreground">
      <MCPVersionDisplay versions={row.versions} />
    </span>
  );
}

export function MCPVersionDisplay({
  versions,
}: MCPVersionDisplayProps): ReactElement {
  // Primary = first existing (non-null) version, not necessarily versions[0].
  const primary = versions.find(v => v != null);

  // No versions or all null
  if (primary == null) {
    return <span>—</span>;
  }

  // Single version - truncate with a clip-only tooltip showing the full string.
  if (versions.length === 1) {
    return <TruncatedText>{primary}</TruncatedText>;
  }

  // Multiple versions - truncate the primary; the +N badge carries the
  // all-versions tooltip and never shrinks.
  return (
    <div className="flex items-center gap-1 min-w-0">
      <TruncatedText className="w-auto min-w-0">{primary}</TruncatedText>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className="text-xs px-1.5 py-0 cursor-help shrink-0"
          >
            +{versions.length - 1}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <div className="font-medium">All versions:</div>
            {versions.map((v, i) => (
              <div key={i} className="text-sm">
                {v || 'Unknown'}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
